import { useState, useEffect } from "react";
import { Shield, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { SectionCardSkeleton, toast } from "@/shared/components";
import { supabase } from "@/shared/lib/supabase";
import { useAuth } from "@/shared/hooks/use-auth";
import { LoginForm } from "./login-form";

interface AuthorizationDetails {
  client?: { name?: string; redirect_uri?: string };
  scopes?: string[];
}

export function McpConsentFlow() {
  const { user, loading: authLoading } = useAuth();
  const params = new URLSearchParams(window.location.search);
  const authorizationId = params.get("authorization_id");

  if (!authorizationId) {
    return (
      <div className="rounded-2xl border border-stroke-subtle bg-surface-raised p-8 text-center">
        <XCircle className="mx-auto mb-3 size-10 text-signal-danger" />
        <h2 className="text-lg font-bold">잘못된 요청</h2>
        <p className="mt-2 text-[13px] text-text-tertiary">
          authorization_id가 누락되었습니다.
        </p>
      </div>
    );
  }

  if (authLoading) {
    return <SectionCardSkeleton className="rounded-2xl" />;
  }

  if (!user) {
    return <LoginForm />;
  }

  return (
    <ConsentForm authorizationId={authorizationId} userEmail={user.email} />
  );
}

function ConsentForm({
  authorizationId,
  userEmail,
}: {
  authorizationId: string;
  userEmail?: string;
}) {
  const [details, setDetails] = useState<AuthorizationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDetails();
  }, [authorizationId]);

  async function loadDetails() {
    try {
      const result =
        await supabase.auth.oauth.getAuthorizationDetails(authorizationId);
      if (result.error) {
        console.error(
          "[consent] getAuthorizationDetails error:",
          JSON.stringify(result.error),
        );
        throw result.error;
      }

      const data = result.data as any;
      const autoRedirect = data?.redirect_url ?? data?.redirect_to;
      if (autoRedirect) {
        window.location.href = autoRedirect;
        return;
      }

      setDetails(data as AuthorizationDetails);
    } catch (err: any) {
      const msg = err.message || err.msg || JSON.stringify(err);
      console.error("[consent] loadDetails failed:", msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove() {
    setSubmitting(true);
    try {
      const result =
        await supabase.auth.oauth.approveAuthorization(authorizationId);
      if (result.error) throw result.error;
      const rd =
        (result.data as any)?.redirect_url ?? (result.data as any)?.redirect_to;
      if (rd) {
        window.location.href = rd;
      }
    } catch (err: any) {
      toast.error(err.message || "승인 처리에 실패했습니다");
      setSubmitting(false);
    }
  }

  async function handleDeny() {
    setSubmitting(true);
    try {
      const result =
        await supabase.auth.oauth.denyAuthorization(authorizationId);
      if (result.error) throw result.error;
      const rd =
        (result.data as any)?.redirect_url ?? (result.data as any)?.redirect_to;
      if (rd) {
        window.location.href = rd;
      }
    } catch (err: any) {
      toast.error(err.message || "거부 처리에 실패했습니다");
      setSubmitting(false);
    }
  }

  if (loading) {
    return <SectionCardSkeleton className="rounded-2xl" />;
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-stroke-subtle bg-surface-raised p-8 text-center">
        <XCircle className="mx-auto mb-3 size-10 text-signal-danger" />
        <h2 className="text-lg font-bold">인증 요청 오류</h2>
        <p className="mt-2 text-[13px] text-text-tertiary">{error}</p>
        <p className="mt-3 text-[11px] text-text-tertiary/60">
          authorization_id: {authorizationId.slice(0, 8)}...
        </p>
      </div>
    );
  }

  const appName = details?.client?.name ?? "외부 앱";
  const scopes = details?.scopes ?? ["openid", "profile", "email"];

  return (
    <div className="rounded-2xl border border-stroke-subtle bg-surface-raised p-8">
      <div className="mb-6 text-center">
        <Shield className="mx-auto mb-2 size-8 text-text-brand" />
        <h1 className="text-xl font-bold">앱 접근 승인</h1>
        <p className="mt-1 text-[13px] text-text-tertiary">
          <strong className="text-text-primary">{appName}</strong>이(가) 다음
          권한을 요청합니다.
        </p>
      </div>

      <div className="mb-4 rounded-lg bg-surface-sunken/50 p-3 text-[13px]">
        <span className="text-text-tertiary">로그인 계정: </span>
        <span className="font-medium">{userEmail}</span>
      </div>

      <div className="mb-6 space-y-2">
        {scopes.map((scope) => (
          <div
            key={scope}
            className="flex items-center gap-2 rounded-lg border border-stroke-subtle px-3 py-2 text-[13px]"
          >
            <CheckCircle className="size-4 text-text-brand" />
            <span>{scopeLabel(scope)}</span>
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <button
          onClick={handleDeny}
          disabled={submitting}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-stroke-subtle py-2.5 text-[13px] font-medium text-text-tertiary transition-colors hover:bg-surface-sunken disabled:opacity-50"
        >
          <XCircle className="size-4" />
          거부
        </button>
        <button
          onClick={handleApprove}
          disabled={submitting}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-accent py-2.5 text-[13px] font-semibold text-text-inverse transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <CheckCircle className="size-4" />
          )}
          {submitting ? "처리 중..." : "승인"}
        </button>
      </div>
    </div>
  );
}

function scopeLabel(scope: string): string {
  switch (scope) {
    case "openid":
      return "기본 인증 정보";
    case "profile":
      return "프로필 정보 (이름, 아바타)";
    case "email":
      return "이메일 주소";
    case "phone":
      return "전화번호";
    default:
      return scope;
  }
}
